import { socialEmbedUrl } from "@ui/utils";
import { useState } from "react";
import type { EmbedCardData } from "./types";

type Props = Pick<
	EmbedCardData,
	"cover" | "videoUrl" | "sourceUrl" | "platform" | "name"
> & {
	emptyLabel: string;
};

/** Poster / autoplay clip / embed / "no photo" fallback shared by every EmbedCard variant. */
export function EmbedMedia({
	cover,
	videoUrl,
	sourceUrl,
	platform,
	name,
	emptyLabel,
}: Props) {
	const [failed, setFailed] = useState(false);

	if (videoUrl) {
		return (
			<video
				autoPlay
				loop
				muted
				playsInline
				preload="metadata"
				aria-label={name}
				className="absolute inset-0 h-full w-full object-cover"
			>
				<source src={videoUrl} />
			</video>
		);
	}

	// Temporary stand-in until CF-7 lands a hosted video file (only passed for
	// the feature card - see EmbedCard). Revert once videoUrl is real.
	const iframeSrc =
		platform && sourceUrl ? socialEmbedUrl(platform, sourceUrl) : null;
	if (iframeSrc) {
		return (
			<iframe
				src={iframeSrc}
				title={name}
				className="absolute inset-0 h-full w-full border-0 bg-white"
				allow="autoplay; encrypted-media; picture-in-picture"
				allowFullScreen
			/>
		);
	}

	if (cover && cover.trim().length > 0 && !failed) {
		return (
			<img
				src={cover}
				alt={name}
				onError={() => setFailed(true)}
				className="absolute inset-0 h-full w-full object-cover"
			/>
		);
	}

	return (
		<div className="absolute inset-0 grid place-items-center">
			<span className="px-2 text-center text-[11px] font-medium text-[var(--content-faint)]">
				{emptyLabel}
			</span>
		</div>
	);
}
